import React, { useMemo, useState } from "react";
import { Card } from "primereact/card";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Dropdown } from "primereact/dropdown";
import { Tag } from "primereact/tag";
import MonthlyTimeSeries from "../../components/graphs/MonthlyTimeSeries";
import ReusableBarChart from "../../components/graphs/ReusableBarChart";
import { PRIMARY_COLOR } from "../../../styles/theme_constants";
import type { ReportingOptions } from "../../../types/stationPerformance";
import { useStationDetail } from "../hooks/useStationDetail";
import { DISTRIBUTION_MEASURE_OPTIONS } from "../StationPerformance.consts";
import SpreadStatisticsPanel from "./SpreadStatisticsPanel";
import MeasureValueCell from "./MeasureValueCell";

interface StationDetailProps {
  unitId: string | null;
  month: string;
  options: ReportingOptions;
}

/** "2026-06" → { year, month } for the shared chart. */
const toMonthKey = (yearMonth: string) => {
  const [year, monthPart] = yearMonth.split("-");
  return { year: Number(year), month: Number(monthPart) };
};

/**
 * One station over time, and inside a month (US1, US5, US6).
 *
 * Two questions on one screen, deliberately: how this station moves month to month, and what its
 * individual sessions look like. An average alone hides the second: a station running a few long
 * charges and many short ones has the same mean as a steady one, and only the spread separates them.
 */
const StationDetail: React.FC<StationDetailProps> = ({ unitId, month, options }) => {
  const [measure, setMeasure] = useState("moneyCents");
  const { acrossMonths, withinMonth, initialLoading, error } = useStationDetail({
    unitId,
    month,
    options,
    measure,
  });

  // Every hook runs unconditionally and before any early return: React requires a stable hook order,
  // and the guards below would otherwise skip these on some renders.
  const series = useMemo(() => acrossMonths?.series ?? [], [acrossMonths]);
  const months = useMemo(() => series.map((p) => toMonthKey(p.yearMonth)), [series]);
  // A trailing OPEN month is dashed by the chart, so a partial month is not read as a decline.
  const finals = useMemo(
    () => series.map((p) => acrossMonths?.context?.monthClosure?.[p.yearMonth] !== "OPEN"),
    [series, acrossMonths],
  );
  const isMoney = measure === "moneyCents" || measure === "moneyPerSessionCents";
  const chartData = useMemo(
    () => series.map((p) => (isMoney ? p.value / 100 : p.value)),
    [series, isMoney],
  );

  if (!unitId) {
    return <Message severity="info" text="Pick a station from the league table to see its detail." />;
  }
  if (initialLoading) {
    return (
      <div className="flex justify-content-center p-5">
        <ProgressSpinner />
      </div>
    );
  }
  if (error) return <Message severity="error" text={error} />;

  return (
    <div className="flex flex-column gap-3">
      <div className="flex flex-wrap justify-content-between align-items-center gap-3">
        <h3 className="m-0">{unitId}</h3>
        <div className="flex flex-column gap-1">
          <label className="text-sm text-color-secondary">Measure</label>
          <Dropdown
            value={measure}
            options={DISTRIBUTION_MEASURE_OPTIONS}
            onChange={(e) => setMeasure(e.value)}
          />
        </div>
      </div>

      <Card>
        {series.length === 0 ? (
          <Message severity="info" text="No monthly history for this station yet." />
        ) : (
          <>
            <MonthlyTimeSeries
              title={`${DISTRIBUTION_MEASURE_OPTIONS.find((m) => m.value === measure)?.label ?? measure} per month`}
              months={months}
              finals={finals}
              series={[
                {
                  label: DISTRIBUTION_MEASURE_OPTIONS.find((m) => m.value === measure)?.label ?? measure,
                  color: PRIMARY_COLOR,
                  data: chartData,
                },
              ]}
            />
            <div className="flex flex-wrap gap-4 mt-3 text-sm">
              {acrossMonths?.bestMonth && (
                <span>
                  <Tag value="Best" severity="success" className="mr-2" />
                  {acrossMonths.bestMonth.yearMonth} ·{" "}
                  {isMoney
                    ? (acrossMonths.bestMonth.value / 100).toLocaleString(undefined, {
                        style: "currency",
                        currency: "EUR",
                      })
                    : acrossMonths.bestMonth.value.toLocaleString()}
                </span>
              )}
              {acrossMonths?.worstMonth && (
                <span>
                  <Tag value="Worst" severity="warning" className="mr-2" />
                  {acrossMonths.worstMonth.yearMonth} ·{" "}
                  {isMoney
                    ? (acrossMonths.worstMonth.value / 100).toLocaleString(undefined, {
                        style: "currency",
                        currency: "EUR",
                      })
                    : acrossMonths.worstMonth.value.toLocaleString()}
                </span>
              )}
              <span className="text-color-secondary">
                Month-to-month variation{" "}
                <MeasureValueCell
                  measure={acrossMonths?.statistics?.coefficientOfVariation}
                  decimals={3}
                />
                <i
                  className="pi pi-info-circle ml-1"
                  style={{ fontSize: "0.7rem" }}
                  title="How much this station swings between months, relative to its own average. High means seasonal or unstable rather than simply large."
                />
              </span>
            </div>
          </>
        )}
      </Card>

      <Card title={`Session spread in ${month}`}>
        {!withinMonth ? (
          <Message severity="info" text="No session spread available for this station and month." />
        ) : (
          <>
            {withinMonth.excluded && withinMonth.excluded.zeroEnergySessions > 0 && (
              <Message
                className="mb-3"
                severity="info"
                text={`${withinMonth.excluded.zeroEnergySessions} session(s) delivered no energy and are excluded from the spread — ${withinMonth.excluded.reason.toLowerCase()}.`}
              />
            )}
            <div className="grid">
              <div className="col-12 md:col-6">
                <SpreadStatisticsPanel
                  title="Energy per session"
                  statistics={withinMonth.statistics}
                  observationCount={withinMonth.observationCount}
                  suffix=" kWh"
                />
              </div>
              <div className="col-12 md:col-6">
                <SpreadStatisticsPanel
                  title="Money per session"
                  statistics={withinMonth.moneyStatistics}
                  observationCount={withinMonth.observationCount}
                  format="money"
                />
              </div>
            </div>
            {withinMonth.dailySessionCounts && withinMonth.dailySessionCounts.length > 0 && (
              <div className="mt-3">
                {/* The chart is the point here: a table of daily counts hides concentration, whereas the
                    shape of the bars shows a weekend-only station at a glance. */}
                <ReusableBarChart
                  title={`Sessions per day in ${month}`}
                  datasetLabels={["Sessions"]}
                  labels={withinMonth.dailySessionCounts.map((_, i) => String(i + 1))}
                  values={withinMonth.dailySessionCounts}
                  unit="sessions"
                />
              </div>
            )}
            {withinMonth.dailySpread && (
              <div className="mt-3">
                <SpreadStatisticsPanel
                  title="Sessions per day"
                  statistics={withinMonth.dailySpread}
                  observationCount={withinMonth.dailySessionCounts?.length}
                />
                <span className="text-color-secondary text-xs">
                  A high variation here means activity is concentrated on particular days — a
                  weekend-only station looks very different from one with steady daily traffic, and a
                  monthly average hides that entirely.
                </span>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default StationDetail;
